import { Collection }                               from '@mathquis/modelx';
import { AppForm }                                  from 'components/App/AppForm';
import type { ReactElement }                        from 'react';
import React                                        from 'react';
import { MailOutlined, ShopOutlined, UserOutlined } from '@ant-design/icons';
import { notification }                             from 'antd';
import type { StandistRole }                        from 'constants/StandistRoleReference';
import { ROLE_STANDIST_AGENCY }                     from 'constants/StandistRoleReference';
import { ROLE_STANDIST_DECORATOR }                  from 'constants/StandistRoleReference';
import { ROLE_STANDIST_EXHIBITOR }                  from 'constants/StandistRoleReference';
import NotificationModel                            from 'models/NotificationModel';
import UserModel                                    from 'models/UserModel';
import UserStandRoleModel                           from 'models/UserStandRoleModel';
import { ApiCollection }                            from 'collections/ApiCollection';
import { ACCESS_LOGIN, EMPTY }                      from 'constants/NotificationReferences';
import FormattedMessageWrapper                      from 'components/App/FormattedMessageWrapper';
import { standDashboardStore }                      from 'stores/index';
import currentSession                               from 'utils/currentSession';
import { type STANDIST_FORM_MODES }                 from 'forms/StandistForm/StandistForm';
import StandistForm                                 from 'forms/StandistForm/StandistForm';
import { MODE_ADD }                                 from 'forms/StandistForm/StandistForm';
import DropDownMenu                                 from 'components/App/DropDownMenu';
import StandForm                                    from 'forms/StandForm';
import SendNotificationForm                         from 'forms/SendNotificationForm';
import rights                                       from 'utils/rights';
import StandModel                                   from 'models/StandModel';
import { formatMessageIntl }                        from 'utils/messageIntl';

interface StandDashboardHeaderDropdownBtnProps {
  additionalItems: any[],
  agencyCollection: Collection<UserModel>;
  decoratorCollection: Collection<UserModel>;
  exhibitorCollection: Collection<UserModel>;
  externalSync: boolean;
  fetchStandistsWithRoles: () => void;
  setConfirmationNotifyVisible: (visible: boolean) => void;
  stand: StandModel;
  userStandRoleCollection: ApiCollection<UserStandRoleModel>;
}

class StandDashboardHeaderDropdownBtn extends React.Component<StandDashboardHeaderDropdownBtnProps> {
  static defaultProps = {
    additionalItems: [],
  };

  public render() {
    return this.renderDropDownMenu();
  }

  public renderDropDownMenu = () => {
    const {
      additionalItems,
      agencyCollection,
      decoratorCollection,
      exhibitorCollection,
      stand,
    } = this.props;

    const items = [];

    if (rights.for.stand.edit) {
      items.push({
        icon: <ShopOutlined />,
        key: 'EDIT_STAND',
        label: <FormattedMessageWrapper id="Modifier stand" />,
        onClick: () => AppForm.open(StandForm, {
          model: stand,
          onSuccess: () => {
            notification.success({
              title: formatMessageIntl("Modification réussie !"),
            });
          },
        }, {
          okText: <FormattedMessageWrapper id="Enregistrer" />,
          title: <FormattedMessageWrapper id="Modifier le stand" />,
        }),
      });
    }

    if (
      rights.for.userStandRole.add
      && !currentSession.isStandist
    ) {
      items.push(
        {
          icon: <UserOutlined />,
          key: 'EDIT_STANDIST_EXHIBITOR',
          label: <FormattedMessageWrapper id="Ajouter exposant" />,
          onClick: () => {
            this._openStandistFormModal(
              <FormattedMessageWrapper id="Ajouter Exposant" />,
              exhibitorCollection,
              new UserModel(),
              ROLE_STANDIST_EXHIBITOR,
            );
          },
        },
        {
          icon: <UserOutlined />,
          key: 'EDIT_STANDIST_DECORATOR',
          label: <FormattedMessageWrapper id="Ajouter décorateur" />,
          onClick: () => {
            this._openStandistFormModal(
              <FormattedMessageWrapper id="Ajouter Décorateur" />,
              decoratorCollection,
              new UserModel(),
              ROLE_STANDIST_DECORATOR,
              MODE_ADD,
            );
          },
        },
        {
          icon: <UserOutlined />,
          key: 'EDIT_STANDIST_AGENCY',
          label: <FormattedMessageWrapper id="Ajouter agence" />,
          onClick: () => {
            this._openStandistFormModal(
              <FormattedMessageWrapper id="Ajouter Agence" />,
              agencyCollection,
              new UserModel(),
              ROLE_STANDIST_AGENCY,
            );
          },
        },
      );
    }

    if (rights.for.notification.add) {
      items.push({
        icon: <MailOutlined />,
        key: 'SEND_NOTIFICATION',
        label: <FormattedMessageWrapper id="Envoyer une notification" />,
        onClick: () => {
          this.props.setConfirmationNotifyVisible(true);
        },
      });
    }

    items.push(...additionalItems);

    if (!items.length) {
      return null;
    }

    return <DropDownMenu items={items} />;
  };

  private _openStandistFormModal = (
    title: ReactElement,
    standistCollection: Collection<UserModel>,
    user: UserModel,
    standistRole: StandistRole,
    mode: STANDIST_FORM_MODES = MODE_ADD,
  ) => {
    const { externalSync, fetchStandistsWithRoles, stand, userStandRoleCollection } = this.props;

    let currentUserStandRole = userStandRoleCollection.find(
      (userStandRole: UserStandRoleModel) => {
        return userStandRole.userIri === user.iri
          && userStandRole.standistRole === standistRole;
      },
    ) as UserStandRoleModel;

    if (!currentUserStandRole) {
      currentUserStandRole = new UserStandRoleModel({
        stand: stand.iri,
        standistRole,
      });
    }

    AppForm.open(
      StandistForm,
      {
        mode,
        onSuccess: (openSendNotificationFormModal: boolean, emailOrPasswordChanged: boolean = false, users: string[] = []) => {
          fetchStandistsWithRoles();

          if (openSendNotificationFormModal) {
            AppForm.open(SendNotificationForm, {
              externalSync: !!externalSync,
              model: new NotificationModel({ users }),
              notificationTag: !emailOrPasswordChanged ? EMPTY.tag : ACCESS_LOGIN.tag,
              selectedRows: [stand],
              standCollectionFull: new ApiCollection(StandModel),
            }, {
              okText: <FormattedMessageWrapper id="Envoyer" />,
              title: <FormattedMessageWrapper id="Envoyer une notification" />,
            });
          }
        },
        stand: standDashboardStore.stand,
        standistCollection,
        standistRole,
        user,
        userStandRole: currentUserStandRole,
      },
      {
        title,
        width: 1024,
      },
    );
  };
}

export default StandDashboardHeaderDropdownBtn;
